/**
 * The inspector when nothing is selected: what is most built upon (hierarchy links), what is
 * most used (the other links), groups, nodes per category, the values of the environment's
 * choice fields (languages in Coding).
 */
import { For, Show, createMemo, type JSX } from "solid-js";
import { useApp } from "../../app/context";
import { groupChoices, nodesInGroup, overview } from "../../shared/analysis";
import { CATEGORY_COLOURS } from "../../shared/categories";
import { Group, NodeRef } from "../../shared/ui";
import { useOpenNode } from "./openNode";

/** Summary of the whole graph. */
export function OverviewPanel(): JSX.Element {
  const { graph, view, kinds, fields } = useApp();
  const open = useOpenNode();
  const o = createMemo(() => overview(graph.state.graph, kinds.available(), fields.list()));
  const groups = createMemo(() => groupChoices(graph.state.graph));
  return (
    <>
      <div class="ihead">
        <h2 class="ov">Overview</h2>
        <p class="note">
          Click a node to see its links, the chain it builds on, its source and comments. Search a name or a language to highlight the nodes that match. Click a link to change
          its kind, reverse it or delete it. Arrows point from a node to what it builds on or uses.
        </p>
      </div>
      <Group title="Most built upon" count={o().builtUpon.length}>
        <For each={o().builtUpon}>{(c) => <NodeRef node={c.node} extra={`${c.count} built on it`} onOpen={() => open(c.node.id)} />}</For>
      </Group>
      <Group title="Most used" count={o().mostUsed.length}>
        <For each={o().mostUsed}>{(c) => <NodeRef node={c.node} extra={`${c.count} ${c.count === 1 ? "user" : "users"}`} onOpen={() => open(c.node.id)} />}</For>
      </Group>
      <Group title="Groups" count={groups().length}>
        <For each={groups()}>
          {(c) => (
            <li>
              <button type="button" class="ref tree-ref" style={{ "--depth": c.depth }} data-go-group={c.id} onClick={() => view.selectGroup(c.id)}>
                <span>{c.label.trim()}</span>
                <em>{nodesInGroup(graph.state.graph, c.id, true).length} nodes</em>
              </button>
            </li>
          )}
        </For>
      </Group>
      <Show when={o().perCategory.length}>
        <section class="grp">
          <h3>Nodes per category</h3>
          <table class="counts" id="overview-categories">
            <tbody>
              <For each={o().perCategory}>
                {(row, at) => (
                  <tr>
                    <td>
                      <i class="dot" style={{ background: `var(--category-${(at() % CATEGORY_COLOURS) + 1})` }} />
                      {row.name}
                    </td>
                    <td>{row.count}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </section>
      </Show>
      <For each={o().values}>
        {(counted) => (
          <section class="grp" data-overview-field={counted.field.id}>
            <h3>{counted.field.label}</h3>
            <table class="counts">
              <tbody>
                <For each={counted.values}>
                  {(row) => (
                    <tr>
                      <td class="mono">{row.value}</td>
                      <td>{row.count}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </section>
        )}
      </For>
    </>
  );
}
